import React, { useState, useRef } from 'react';
import { authService } from '../../../services/api';

/* ─── Phase constants ─────────────────────────────── */
const PHASE_EMAIL    = 'email';
const PHASE_OTP      = 'otp';
const PHASE_PHONE    = 'phone';

const OTP_LENGTH = 6;

/* ─── Spinner ─────────────────────────────────────── */
const Spinner = () => <span className="ro-spin ro-spin--sm" aria-hidden />;

/* ═══════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════ */
const StepRestaurantAuth = ({ onAuthComplete, onBack }) => {
    const [phase,    setPhase]    = useState(PHASE_EMAIL);
    const [email,    setEmail]    = useState('');
    const [otp,      setOtp]      = useState(Array(OTP_LENGTH).fill(''));
    const [mobile,   setMobile]   = useState('');
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState('');

    const otpRefs = useRef([]);

    /* ══════════════════════════════════════════════
       PHASE 1 — Email → enviar OTP directo
       ══════════════════════════════════════════════ */
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim().toLowerCase();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError('Ingresa un correo electrónico válido');
            return;
        }
        if (!trimmed.endsWith('@gmail.com')) {
            setError('Solo aceptamos correos de Gmail (@gmail.com)');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await authService.sendOtp(trimmed);
            if (res?.message === 'otp_already_active') {
                setEmail(trimmed);
                setPhase(PHASE_OTP);
                return;
            }
            if (res?.success === false) {
                throw new Error(res?.message || 'Error al enviar el código.');
            }
            setEmail(trimmed);
            setPhase(PHASE_OTP);
        } catch (err) {
            const data = err?.response?.data;
            if (data?.message === 'otp_already_active') {
                setEmail(trimmed);
                setPhase(PHASE_OTP);
            } else {
                const msg = data?.message || err?.message || 'Error al enviar el código. Inténtalo de nuevo.';
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    /* ══════════════════════════════════════════════
       PHASE 2 — OTP verify
       ══════════════════════════════════════════════ */
    const handleOtpChange = (idx, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...otp];
        next[idx] = digit;
        setOtp(next);
        setError('');
        if (digit && idx < OTP_LENGTH - 1) {
            otpRefs.current[idx + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (idx, e) => {
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && idx > 0) otpRefs.current[idx - 1]?.focus();
        if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus();
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill('');
        for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
        setOtp(next);
        const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        otpRefs.current[focusIdx]?.focus();
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        const code = otp.join('');
        if (code.length < OTP_LENGTH) {
            setError('Ingresa el código completo de 6 dígitos');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await authService.verifyOtp(email, code);
            if (res?.success === false) {
                throw new Error(res?.message || 'Código incorrecto.');
            }
            setPhase(PHASE_PHONE);
        } catch (err) {
            setError('Código incorrecto. Inténtalo de nuevo.');
            setOtp(Array(OTP_LENGTH).fill(''));
            otpRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    /* ══════════════════════════════════════════════
       PHASE 3 — Phone number (+593)
       ══════════════════════════════════════════════ */
    const handleMobileInput = (raw) => {
        const digits = raw.replace(/\D/g, '').replace(/^0+/, '').slice(0, 9);
        setMobile(digits);
        setError('');
    };

    const handlePhoneSubmit = (e) => {
        e.preventDefault();
        if (mobile.length !== 9) {
            setError('El número debe tener 9 dígitos (ej. 991234567)');
            return;
        }
        setError('');
        onAuthComplete(email, mobile);
    };

    /* ══════════════════════════════════════════════
       Render helpers
       ══════════════════════════════════════════════ */
    const renderEmail = () => (
        <form onSubmit={handleEmailSubmit} noValidate>
            <div className="ro-auth-hd">
                <div className="ro-auth-verify-ico">🏪</div>
                <h2 className="ro-auth-title">Registra tu negocio</h2>
                <p className="ro-auth-sub">
                    Ingresa tu correo para comenzar. Te enviaremos un código de verificación.
                </p>
            </div>

            <div className="ro-auth-field">
                <label className="ro-auth-label" htmlFor="auth-email">Correo electrónico</label>
                <input
                    id="auth-email"
                    type="email"
                    className={`ro-auth-input${error ? ' ro-auth-input--err' : ''}`}
                    placeholder="tuemail@gmail.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    autoFocus
                    autoComplete="email"
                />
                {error && <span className="ro-err-msg">{error}</span>}
            </div>

            <button type="submit" className="ro-auth-submit" disabled={loading}>
                {loading ? <><Spinner /> Enviando código...</> : <>Continuar →</>}
            </button>

            <p className="ro-auth-legal">
                Al continuar aceptas nuestros{' '}
                <a href="/terminos" target="_blank" rel="noopener noreferrer">Términos de uso</a>
                {' '}y{' '}
                <a href="/privacidad" target="_blank" rel="noopener noreferrer">Política de privacidad</a>.
            </p>

            <button type="button" className="ro-auth-back-link" onClick={onBack}>
                ← Volver al inicio
            </button>
        </form>
    );

    const renderOtp = () => (
        <form onSubmit={handleOtpSubmit} noValidate>
            <div className="ro-auth-hd">
                <div className="ro-auth-verify-ico">📩</div>
                <h2 className="ro-auth-title">Revisa tu correo</h2>
                <p className="ro-auth-sub">
                    Enviamos un código de 6 dígitos a <strong>{email}</strong>.<br />
                    Expira en 10 minutos.
                </p>
            </div>

            <div className="ro-auth-code-row" onPaste={handleOtpPaste}>
                {otp.map((digit, idx) => (
                    <input
                        key={idx}
                        ref={(el) => (otpRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={`ro-auth-code-box${error ? ' ro-auth-code-box--err' : ''}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        autoFocus={idx === 0}
                    />
                ))}
            </div>

            {error && <span className="ro-err-msg ro-err-msg--center">{error}</span>}

            <button type="submit" className="ro-auth-submit" disabled={loading || otp.join('').length < OTP_LENGTH}>
                {loading ? <><Spinner /> Verificando...</> : <>Verificar código</>}
            </button>

            <button
                type="button"
                className="ro-auth-back-link"
                onClick={() => { setPhase(PHASE_EMAIL); setError(''); setOtp(Array(OTP_LENGTH).fill('')); }}
            >
                ← Cambiar correo
            </button>
        </form>
    );

    const renderPhone = () => (
        <form onSubmit={handlePhoneSubmit} noValidate>
            <div className="ro-auth-hd">
                <div className="ro-auth-verify-ico">📱</div>
                <h2 className="ro-auth-title">Tu número de celular</h2>
                <p className="ro-auth-sub">
                    Ingresa tu número de celular para continuar con el registro.
                </p>
            </div>

            <div className="ro-auth-field">
                <label className="ro-auth-label" htmlFor="auth-phone">Número de celular</label>
                <div className="ro-auth-phone-wrap">
                    <span className="ro-auth-phone-prefix">🇪🇨 +593</span>
                    <input
                        id="auth-phone"
                        type="tel"
                        inputMode="numeric"
                        className={`ro-auth-input ro-auth-input--phone${error ? ' ro-auth-input--err' : ''}`}
                        placeholder="Ej. 991234567"
                        value={mobile}
                        onChange={(e) => handleMobileInput(e.target.value)}
                        maxLength={9}
                        autoFocus
                    />
                    <span className="ro-auth-phone-count">{mobile.length}/9</span>
                </div>
                {error && <span className="ro-err-msg">{error}</span>}
            </div>

            <button type="submit" className="ro-auth-submit" disabled={mobile.length !== 9}>
                Continuar →
            </button>

            <button
                type="button"
                className="ro-auth-back-link"
                onClick={() => { setPhase(PHASE_EMAIL); setError(''); }}
            >
                ← Cambiar correo
            </button>
        </form>
    );

    /* ══════════════════════════════════════════════
       Layout
       ══════════════════════════════════════════════ */
    return (
        <div className="ro-auth-wrap">
            <div className="ro-auth-card">
                <div className="ro-auth-inner">
                    {phase === PHASE_EMAIL && renderEmail()}
                    {phase === PHASE_OTP   && renderOtp()}
                    {phase === PHASE_PHONE && renderPhone()}
                </div>
            </div>
        </div>
    );
};

export default StepRestaurantAuth;
